import {
  LayoutDashboard, BookOpen, Search, ArrowLeftRight, BookMarked,
  Lightbulb, Scale, Briefcase, FileText, HandHelping, Shield,
  Settings, BarChart3, Home, FileSearch, CalendarCheck, Archive,
  Users, Mail, Clock, NotebookPen, LifeBuoy, Percent, FolderInput, Compass,
  type LucideIcon
} from 'lucide-react';

export type UserMode = 'lawyer' | 'judge' | 'layperson';

export interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  shortcut?: string;
  section?: string;
  /**
   * Capability key into FEATURE_PRACTICE_AREAS. When set, the Sidebar shows
   * this item only if isAvailableFor(feature, effectiveLens) is true.
   * Items without a feature key are always shown.
   */
  feature?: string;
  /**
   * Server flag key (see components/common/navFlags.ts). When set, the item is
   * shown only once the server reports the flag ON (kill switch => absent).
   */
  flag?: string;
  /** @deprecated kept for back-compat; gating now uses `feature`. */
  criminalOnly?: boolean;
}

export interface QuickLink {
  title: string;
  subtitle: string;
  path: string;
}

export interface PersonaConfig {
  navItems: NavItem[];
  toolItems: NavItem[];
  quickLinks: QuickLink[];
  searchPlaceholder: string;
  searchRoute: string;
  heroTitle: string;
  heroSubtitle: string;
}

const PERSONA_CONFIGS: Record<UserMode, PersonaConfig> = {
  layperson: {
    navItems: [
      { path: '/', label: 'Home', icon: Home },
      { path: '/rights', label: 'Know Your Rights', icon: Shield },
      { path: '/legal-aid', label: 'Find Legal Help', icon: HandHelping },
      { path: '/clauses', label: 'Laws Explained', icon: BookOpen },
      { path: '/constitution', label: 'Constitution', icon: BookMarked },
      { path: '/bail', label: 'Bail Information', icon: Scale },
    ],
    toolItems: [
      { path: '/cases', label: 'Court Decisions', icon: Search },
    ],
    quickLinks: [
      { title: 'Your rights during arrest', subtitle: 'Step-by-step guide', path: '/rights' },
      { title: 'How to file an FIR', subtitle: 'Police complaint process', path: '/rights' },
      { title: 'Free legal help', subtitle: 'Find lawyers near you', path: '/legal-aid' },
    ],
    searchPlaceholder: 'Search for legal help in simple words...',
    searchRoute: '/rights',
    heroTitle: 'Understand Your Legal Rights',
    heroSubtitle: 'Legal information made simple — no legal background needed',
  },

  lawyer: {
    navItems: [
      // My Work — the advocate's own daily surface, anchored at the top so the
      // eye lands on their work first. Order set by product owner (2026-08-17):
      // Dashboard → Calendar → Clients → Matters → Cause List → My Diary.
      // Start Here placed above Dashboard by the owner (2026-10-30): the
      // "What's on your mind?" situation cards, moved off the home so the
      // Dashboard shows its lists again. Named "Start Here", not "Beginner
      // Guide" — a seniority label would hide daily entry points (FIR, arrest,
      // bail) from exactly the advocates they serve. Criminal workspace only,
      // via the capability map, like the cards it carries.
      { path: '/start-here', label: 'Start Here', icon: Compass, section: 'My Work', feature: 'start_here' },
      { path: '/', label: 'Dashboard', icon: LayoutDashboard, shortcut: '1', section: 'My Work' },
      // Bring a running practice in: find cases in the court records by the
      // advocate's name or CNR, tick the ones that are theirs. No `feature` key —
      // every lawyer sees it; the page itself hides when the kill switch is off.
      { path: '/bring-your-cases', label: 'Bring your cases', icon: FolderInput, shortcut: '8', section: 'My Work', flag: 'bring_cases' },
      // Criminal AND civil: the route guard allows both and the board picks its
      // ranking from the active role (liberty clocks vs time-bar). Keeps main's
      // `feature` key — that is what the capability map and the help registry
      // read — but drops `criminalOnly`, which is unread today and documented
      // the opposite of the truth once civil got its own board.
      { path: '/calendar', label: 'Calendar', icon: CalendarCheck, shortcut: '2', section: 'My Work', feature: 'calendar' },
      { path: '/clients', label: 'Clients', icon: Users, shortcut: '3', section: 'My Work' },
      { path: '/portfolio', label: 'Matters', icon: Briefcase, shortcut: '4', section: 'My Work' },
      // Cause List sits in My Work, not Court Prep: it is the advocate's own
      // diarised dates (matters.next_hearing_date + case_deadlines aggregated by
      // MatterService.cause_list), not a court-registry feed. A daily-work
      // question, not a preparation artifact (product owner, 2026-08-17).
      { path: '/cause-list', label: 'Cause List', icon: CalendarCheck, section: 'My Work' },
      { path: '/day-book', label: 'My Diary', icon: NotebookPen, section: 'My Work' },
      // Brief Bank — the advocate's own templates and bench notes, private to
      // them (criminal-only launch, same gating as /templates).
      { path: '/brief-bank', label: 'Brief Bank', icon: BookMarked, section: 'My Work', criminalOnly: true },
      // Case Analysis — criminal
      { path: '/fir', label: 'Analyze FIR', icon: FileSearch, shortcut: '5', section: 'Case Analysis', feature: 'fir_analyzer', criminalOnly: true },
      // NOT criminalOnly since the civil-suit-spine reland: the engine carries a
      // civil builder family and `case_theory` is mapped to SUIT_PRACTICE_AREAS in
      // types/practiceArea.ts. Visibility is decided there, by navItemVisible —
      // this flag is never read (grep: declared 7x, consumed 0x), so leaving it
      // true would have been documentation asserting the opposite of the gate.
      { path: '/theories', label: 'Build Case Theory', icon: Lightbulb, shortcut: '6', section: 'Case Analysis', feature: 'case_theory' },
      // "Checklist", not "Calculator": bail is argued, not computed. A tool
      // that appears to output a bail *prediction* loses the confidence of
      // exactly the senior advocates whose endorsement carries the product
      // (advocate review, 2026-08-10). The underlying feature is unchanged.
      { path: '/bail', label: 'Bail Grounds Checklist', icon: Scale, shortcut: '7', section: 'Case Analysis', feature: 'bail_calculator', criminalOnly: true },
      // Case Analysis — consumer/civil (shown only under the civil lens)
      { path: '/notice-analyzer', label: 'Analyze Notice', icon: Mail, section: 'Case Analysis', feature: 'notice_analyzer' },
      { path: '/forum-calculator', label: 'Forum & Limitation', icon: Clock, section: 'Case Analysis', feature: 'forum_calculator' },
      // Deliberately keyed off `forum_calculator` rather than a dedicated
      // `interest_calculator` key. The two deterministic civil calculators want
      // exactly the same capability set (SUIT_PRACTICE_AREAS), and a NEW key
      // with no entry in types/practiceArea.ts would fail OPEN — isAvailableFor
      // returns true for unmapped features, so it would show on the criminal
      // rail while App.tsx's RoleRoute bounced the click back to '/'. A nav
      // gate that fails open beside a route guard that fails closed is a dead
      // end. Give it its own key the moment FEATURE_PRACTICE_AREAS gains a
      // matching 'interest_calculator' entry.
      { path: '/interest-calculator', label: 'Interest Calculator', icon: Percent, section: 'Case Analysis', feature: 'forum_calculator' },
      // Court Preparation
      { path: '/vault', label: 'My Library', icon: Archive, section: 'Court Preparation' },
      // Hearing Preparation is reached from the matter ("Prepare for the
      // <date> hearing" on the Next hearing cell) and from the Prepare action
      // on a cause-list hearing — both of which already know WHICH hearing.
      // The rail could not: it had to open a matter picker first, teaching the
      // wrong mental model. Route stays live; catalogued with nav_hidden=True.
      // { path: '/hearing-prep', label: 'Hearing Preparation', icon: CalendarCheck, section: 'Court Preparation', feature: 'hearing_prep', criminalOnly: true },
      { path: '/templates', label: 'Draft Documents', icon: FileText, section: 'Court Preparation' },
      // Legal Research — reference tools, sunk to the bottom (reached least
      // often). Mapper first: IPC→BNS is the highest-frequency, lowest-thought
      // lookup during the transition, and it is deterministic, not AI.
      { path: '/mappings', label: 'IPC → BNS Mapper', icon: ArrowLeftRight, section: 'Legal Research' },
      // { path: '/research', label: 'AI Legal Assistant', icon: Search, shortcut: '8', section: 'Legal Research' }, // Removed from launch (2026-10-20) — duplicated Search Cases / Search Laws / the mapper / the bail checklist and knew no matter. Route stays live; catalogued with nav_hidden=True.
      { path: '/clauses', label: 'Search Laws', icon: BookOpen, shortcut: '9', section: 'Legal Research' },
      { path: '/cases', label: 'Search Cases', icon: Search, section: 'Legal Research' },
      { path: '/constitution', label: 'Ask Constitution', icon: BookMarked, section: 'Legal Research' },
      // { path: '/legal-aid', label: 'Legal Aid', icon: HandHelping, section: 'Legal Research' }, // Removed from launch (2026-08-17) — legal_aid_providers table is empty and no seed script exists in backend/scripts/; every search renders an empty page. Same reason Know Your Rights is deferred.
      // { path: '/rights', label: 'Know Your Rights', icon: Shield, section: 'Research' }, // Deferred from launch — rights_guides table empty, content needs advocate gate (2026-08-10)
      // Help — the manuals for the workspace, last in the rail. Sections render
      // in first-appearance order, so this sits below Research.
      { path: '/civil-guide', label: 'Civil Practice Guide', icon: LifeBuoy, section: 'Help', feature: 'civil_guide' },
    ],
    toolItems: [],
    quickLinks: [
      { title: 'BNS Section 103', subtitle: 'Murder', path: '/clauses/bns/103' },
      { title: 'BNS Section 63', subtitle: 'Rape', path: '/clauses/bns/63' },
      { title: 'BNS Section 318', subtitle: 'Cheating', path: '/clauses/bns/318' },
      // Carries its query: a bare /constitution opened an empty search (QA Q55).
      // ConstitutionPage must read ?q= for this to prefill — see the A3 report.
      { title: 'Article 21', subtitle: 'Right to Life', path: '/constitution?q=Article%2021' },
    ],
    searchPlaceholder: 'Search clauses, cases, or legal concepts...',
    searchRoute: '/clauses',
    heroTitle: 'Professional Legal Research',
    heroSubtitle: 'Advanced tools for case analysis, precedent research, and case preparation',
  },

  judge: {
    navItems: [
      { path: '/', label: 'Dashboard', icon: LayoutDashboard, shortcut: '1' },
      { path: '/cases', label: 'Search Cases', icon: Search, shortcut: '2', section: 'Research' },
      { path: '/clauses', label: 'Search Laws', icon: BookOpen, shortcut: '3', section: 'Research' },
      { path: '/constitution', label: 'Constitution', icon: BookMarked, shortcut: '4', section: 'Research' },
      // See the note on the other /theories entry — civil-suit mode means this is
      // no longer criminal-only.
      { path: '/theories', label: 'Case Theory', icon: Lightbulb, shortcut: '5', section: 'Analysis', feature: 'case_theory' },
      { path: '/bail', label: 'Bail Calculator', icon: Scale, shortcut: '6', section: 'Analysis', feature: 'bail_calculator', criminalOnly: true },
      { path: '/portfolio', label: 'Matters', icon: Briefcase, section: 'Management' },
      { path: '/templates', label: 'Draft Documents', icon: FileText, section: 'Management' },
    ],
    toolItems: [],
    quickLinks: [
      { title: 'BNS Section 103', subtitle: 'Murder', path: '/clauses/bns/103' },
      { title: 'BNS Section 63', subtitle: 'Rape', path: '/clauses/bns/63' },
      { title: 'Constitution', subtitle: 'Fundamental Rights', path: '/constitution' },
      { title: 'Bail Guidelines', subtitle: 'Standard bail factors', path: '/bail' },
    ],
    searchPlaceholder: 'Search precedents, judgments, or provisions...',
    searchRoute: '/cases',
    heroTitle: 'Bench Research & Precedent Tools',
    heroSubtitle: 'Quick access to precedents, judgments, and statutory references for bench decisions',
  },
};

export const getPersonaConfig = (mode: UserMode): PersonaConfig => {
  return PERSONA_CONFIGS[mode] || PERSONA_CONFIGS.lawyer;
};

export const getModeDisplayName = (mode: UserMode): string => {
  const names: Record<UserMode, string> = {
    layperson: 'Simple Mode',
    lawyer: 'Lawyer Mode',
    judge: 'Judge Mode',
  };
  return names[mode] || 'Lawyer Mode';
};

export const getModeColor = (mode: UserMode): string => {
  const colors: Record<UserMode, string> = {
    layperson: 'bg-jade-100 text-jade-700 border-jade-200 dark:bg-jade-900/30 dark:text-jade-400 dark:border-jade-800',
    lawyer: 'bg-legal-green-50 text-legal-green-600 border-legal-green-100 dark:bg-legal-green-900/30 dark:text-legal-green-300 dark:border-legal-green-800',
    judge: 'bg-gold-100 text-gold-700 border-gold-200 dark:bg-gold-900/30 dark:text-gold-400 dark:border-gold-800',
  };
  return colors[mode] || colors.lawyer;
};

export const getModeAccent = (mode: UserMode): string => {
  const accents: Record<UserMode, string> = {
    layperson: 'jade',
    lawyer: 'legal-green',
    judge: 'gold',
  };
  return accents[mode] || 'legal-green';
};
